/**
 * Duration Utility Functions
 *
 * Provides parsing and formatting for duration values.
 * Durations are stored as milliseconds internally.
 *
 * Supported input formats:
 * - "3h" - 3 hours
 * - "30m" - 30 minutes
 * - "3h 30m" or "3h30m" - 3 hours and 30 minutes
 * - "3h+30m" - expression with addition
 * - "3h-30m" - expression with subtraction
 * - "-30m" - negative duration
 */

/**
 * Parse a single duration term (no operators).
 * @param input - Duration string like "3h", "30m", "3h 30m"
 * @returns Duration in milliseconds, or null if invalid
 */
function parseSingleDuration(input: string): number | null {
  const trimmed = input.trim();
  if (!trimmed) return null;

  const isNegative = trimmed.startsWith('-');
  const cleanInput = trimmed.replace(/^-/, '');

  // Match hours and minutes with unit suffixes
  const hourMatch = cleanInput.match(/(\d+)\s*h/i);
  const minuteMatch = cleanInput.match(/(\d+)\s*m/i);

  const hours = hourMatch ? parseInt(hourMatch[1], 10) : 0;
  const minutes = minuteMatch ? parseInt(minuteMatch[1], 10) : 0;

  if (hours > 0 || minutes > 0) {
    const result = (hours * 3600 + minutes * 60) * 1000;
    return isNegative ? -result : result;
  }

  // Try parsing as "H M" format (space-separated hours and minutes)
  const parts = cleanInput.split(/\s+/).filter((p) => p.length > 0);
  if (parts.length === 2) {
    const h = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    if (!isNaN(h) && !isNaN(m)) {
      const result = (h * 3600 + m * 60) * 1000;
      return isNegative ? -result : result;
    }
  }

  return null;
}

/**
 * Parse a duration string, supporting expressions with + and - operators.
 * @param input - Duration string or expression like "3h+30m", "2h-15m"
 * @returns Duration in milliseconds, or null if invalid
 *
 * @example
 * parseDuration("3h") // 10800000
 * parseDuration("30m") // 1800000
 * parseDuration("3h+30m") // 12600000
 * parseDuration("3h-30m") // 9000000
 * parseDuration("-30m") // -1800000
 */
export function parseDuration(input: string): number | null {
  const trimmed = input.trim();
  if (!trimmed) return null;

  // Check if input contains + or - operators (not at the start)
  if (trimmed.includes('+') || (trimmed.includes('-') && !trimmed.startsWith('-'))) {
    // Parse expression with multiple terms
    const terms: Array<{ sign: number; value: string }> = [];
    let currentTerm = '';
    let currentSign = 1;

    for (let i = 0; i < trimmed.length; i++) {
      const char = trimmed[i];
      if (char === '+') {
        if (currentTerm.trim()) {
          terms.push({ sign: currentSign, value: currentTerm.trim() });
        }
        currentTerm = '';
        currentSign = 1;
      } else if (char === '-') {
        if (currentTerm.trim()) {
          terms.push({ sign: currentSign, value: currentTerm.trim() });
        }
        currentTerm = '';
        currentSign = -1;
      } else {
        currentTerm += char;
      }
    }
    if (currentTerm.trim()) {
      terms.push({ sign: currentSign, value: currentTerm.trim() });
    }

    let total = 0;
    let hasValidTerm = false;
    for (const term of terms) {
      const parsed = parseSingleDuration(term.value);
      if (parsed !== null) {
        total += term.sign * parsed;
        hasValidTerm = true;
      }
    }

    return hasValidTerm ? total : null;
  }

  // Single term
  return parseSingleDuration(trimmed);
}

export interface FormatDurationOptions {
  /**
   * If true, returns "0m" for zero values instead of placeholder.
   * Default: false (returns "—" for zero)
   */
  showZero?: boolean;

  /**
   * Placeholder string for null/zero values.
   * Default: "—"
   */
  placeholder?: string;
}

/**
 * Format a duration in milliseconds to a human-readable string.
 * @param ms - Duration in milliseconds
 * @param options - Formatting options
 * @returns Formatted string like "3h 30m", "3h", "30m", or placeholder
 *
 * @example
 * formatDuration(12600000) // "3h 30m"
 * formatDuration(0) // "—"
 * formatDuration(0, { showZero: true }) // "0m"
 * formatDuration(null) // "—"
 */
export function formatDuration(
  ms: number | null | undefined,
  options: FormatDurationOptions = {}
): string {
  const { showZero = false, placeholder = '—' } = options;

  if (ms === null || ms === undefined) {
    return placeholder;
  }

  if (ms === 0) {
    return showZero ? '0m' : placeholder;
  }

  const absMs = Math.abs(ms);
  const hours = Math.floor(absMs / 3600000);
  const minutes = Math.floor((absMs % 3600000) / 60000);
  const sign = ms < 0 ? '-' : '';

  if (hours > 0 && minutes > 0) {
    return `${sign}${hours}h ${minutes}m`;
  }
  if (hours > 0) {
    return `${sign}${hours}h`;
  }
  if (minutes > 0) {
    return `${sign}${minutes}m`;
  }

  return showZero ? '0m' : placeholder;
}

/**
 * Format a duration for deviation display (always shows value, even for 0).
 * Convenience wrapper around formatDuration with showZero: true.
 */
export function formatDurationForDeviation(ms: number): string {
  return formatDuration(ms, { showZero: true });
}
